import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  PlusCircle,
  RefreshCw,
  ListOrdered,
  History,
  X,
  ShieldAlert,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import { loadAssessments } from "../lib/assessmentStore";

/**
 * /assessment-choice — the gate between the public Lounge and the Cockpit.
 * Two equally weighted choices: register a new AI system, or re-assess an
 * existing one. Re-assessment opens a modal listing every system in the
 * register (4 LWD seeds + every published user system) and routes to
 * /wizard?edit=<id> on selection.
 */

const RISK_FROM_LABEL = (label = "") => {
  const v = label.toLowerCase();
  if (v.includes("hoog")) return "high";
  if (v.includes("midden") || v.includes("medium")) return "medium";
  return "low";
};

const RISK_TONE = {
  high: { color: "#F08080", icon: ShieldAlert, label: "Hoog risico" },
  medium: { color: "#F4C430", icon: ShieldAlert, label: "Midden risico" },
  low: { color: "#9FE2BF", icon: ShieldCheck, label: "Laag risico" },
};

const AssessmentChoice = () => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [userSystems, setUserSystems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setUserSystems(loadAssessments().filter((a) => a.published));
  }, []);

  const allSystems = useMemo(() => {
    const seeds = leeuwardenSystems.map((s) => ({
      id: s.id,
      code: s.code,
      name: s.name,
      tagline: s.tagline,
      risk: RISK_FROM_LABEL(s.riskClass),
      riskLabel: s.riskClass,
      lifecycleStage: s.lifecycleStage,
      isSeed: true,
    }));
    const usr = userSystems.map((u) => ({
      id: u.id,
      code: u.code,
      name: u.name,
      tagline: u.domain || "Algemeen",
      risk: u.risk,
      riskLabel: u.riskLabel,
      lifecycleStage: u.lastUpdated ? "Bijgewerkt" : "Gepubliceerd",
      isSeed: false,
    }));
    return [...seeds, ...usr];
  }, [userSystems]);

  const handlePickSystem = (id) => {
    setPickerOpen(false);
    navigate(`/wizard?edit=${id}`);
  };

  return (
    <div data-testid="page-assessment-choice">
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.85} />

        <div className="rk-container relative pt-16 md:pt-24 pb-24">
          <div className="overline mb-6" data-testid="assessment-eyebrow">
            Assessment · Continu Gecertificeerd AI
          </div>

          <h1
            className="text-white tracking-tight leading-[1.05] max-w-5xl text-5xl md:text-7xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="assessment-title"
          >
            Hoe wilt u{" "}
            <em
              className="italic"
              style={{
                fontFamily: "Fraunces, serif",
                fontWeight: 300,
                color: "#5BC0EB",
              }}
            >
              verder
            </em>
            ?
          </h1>

          <p
            className="mt-8 text-lg md:text-xl text-[#D4D8DF] max-w-3xl leading-relaxed"
            data-testid="assessment-description"
          >
            Kies of u een nieuw AI-systeem registreert voor een eerste
            compliance-toets, of een bestaand systeem opnieuw beoordeelt na
            bijvoorbeeld een modelupdate of nieuwe regelgeving.
          </p>

          {/* Two-option grid */}
          <div
            className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#C0C5CE]/20"
            data-testid="assessment-options"
          >
            {/* Option A — New AI system */}
            <article
              data-testid="option-new"
              className="bg-[#162E4A] hover:bg-[#18335A] transition-colors duration-500 p-10 md:p-12 flex flex-col"
            >
              <div className="flex items-start justify-between gap-6 mb-8">
                <div className="overline">Optie A · Nieuw</div>
                <div
                  className="w-14 h-14 border border-[#C0C5CE]/40 flex items-center justify-center shrink-0"
                  aria-hidden
                >
                  <PlusCircle
                    strokeWidth={1}
                    className="w-7 h-7 text-[#C0C5CE]"
                  />
                </div>
              </div>

              <h2
                className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              >
                Nieuw AI-systeem registreren
              </h2>

              <p className="mt-5 text-[#D4D8DF] leading-relaxed">
                Voor een algoritme dat nog niet in het register staat. We leggen
                de basis vast: doel, grondslag, data, risico, en de eerste
                IAMA-toets. Resultaat: een gepubliceerd dossier dat klaar is voor
                continu toezicht.
              </p>

              <div className="hairline my-7" />

              <div className="flex items-start gap-3 text-sm text-[#9AA4B2]">
                <ListOrdered
                  strokeWidth={1}
                  className="w-4 h-4 mt-0.5 text-[#C0C5CE] shrink-0"
                />
                <span>
                  <span className="text-[#D4D8DF]">Start intake</span> · 6 stappen
                  · circa 25 min.
                </span>
              </div>

              <div className="mt-auto pt-10">
                <Link
                  to="/wizard"
                  className="rk-btn-cyan"
                  data-testid="option-new-cta"
                >
                  Start intake
                  <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
                </Link>
              </div>
            </article>

            {/* Option B — Re-assess existing system */}
            <article
              data-testid="option-update"
              className="bg-[#162E4A] hover:bg-[#18335A] transition-colors duration-500 p-10 md:p-12 flex flex-col"
            >
              <div className="flex items-start justify-between gap-6 mb-8">
                <div className="overline">Optie B · Update</div>
                <div
                  className="w-14 h-14 border border-[#C0C5CE]/40 flex items-center justify-center shrink-0"
                  aria-hidden
                >
                  <RefreshCw
                    strokeWidth={1}
                    className="w-7 h-7 text-[#C0C5CE]"
                  />
                </div>
              </div>

              <h2
                className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              >
                Bestaand AI-systeem updaten
              </h2>

              <p className="mt-5 text-[#D4D8DF] leading-relaxed">
                Voor een algoritme dat al in beheer is bij uw gemeente. Bijvoor­beeld
                na een modelupdate, een datawissel, een nieuwe wettelijke
                verplichting of een geconstateerd incident. Het bestaande dossier
                blijft intact en krijgt een nieuwe versie.
              </p>

              <div className="hairline my-7" />

              <div className="flex items-start gap-3 text-sm text-[#9AA4B2]">
                <History
                  strokeWidth={1}
                  className="w-4 h-4 mt-0.5 text-[#C0C5CE] shrink-0"
                />
                <span>
                  <span className="text-[#D4D8DF]">Selecteer systeem</span> ·
                  behoudt volledige geschiedenis &amp; lineage.
                </span>
              </div>

              <div className="mt-auto pt-10">
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="rk-btn-cyan"
                  data-testid="option-update-cta"
                >
                  Selecteer systeem
                  <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
                </button>
              </div>
            </article>
          </div>

          {/* Reassuring footer line */}
          <p className="mt-12 text-sm text-[#9AA4B2] max-w-3xl">
            Beide routes voldoen aan het VNG-kader, de AI-Verordening en het
            landelijke Algoritmeregister. U kunt de keuze later altijd nog
            aanpassen.
          </p>
        </div>
      </section>

      {pickerOpen && (
        <SystemPickerDialog
          systems={allSystems}
          onCancel={() => setPickerOpen(false)}
          onPick={handlePickSystem}
        />
      )}
    </div>
  );
};

// =============== System picker dialog ===============
const SystemPickerDialog = ({ systems, onCancel, onPick }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Selecteer een bestaand AI-systeem"
    data-testid="system-picker-overlay"
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{
      background: "rgba(15, 30, 51, 0.72)",
      backdropFilter: "blur(6px)",
    }}
    onClick={onCancel}
  >
    <div
      className="relative max-w-2xl w-[94%] max-h-[86vh] overflow-hidden rk-fade-up flex flex-col"
      style={{
        background: "#162E4A",
        border: "1px solid rgba(192,197,206,0.25)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-4 right-4 text-[#9AA4B2] hover:text-white"
        aria-label="Sluit"
        data-testid="system-picker-close"
      >
        <X strokeWidth={1.25} className="w-5 h-5" />
      </button>

      <div className="px-8 pt-10 pb-6">
        <div className="overline mb-3">Bestaand systeem updaten</div>
        <h2
          className="text-3xl text-white tracking-tight leading-tight"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          data-testid="system-picker-title"
        >
          Welk algoritme wilt u{" "}
          <em
            className="italic text-[#D4D8DF]"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            opnieuw beoordelen?
          </em>
        </h2>
        <p className="mt-3 text-sm text-[#9AA4B2] leading-relaxed">
          Het bestaande dossier blijft intact. We pre-vullen de wizard met de
          huidige antwoorden — u hoeft alleen aan te geven wat er veranderd is.
        </p>
      </div>

      <div className="hairline" />

      <ul
        className="overflow-y-auto"
        data-testid="system-picker-list"
      >
        {systems.map((s) => {
          const tone = RISK_TONE[s.risk] || RISK_TONE.medium;
          const ToneIcon = tone.icon;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onPick(s.id)}
                data-testid={`system-picker-item-${s.id}`}
                className="w-full text-left px-8 py-5 flex items-center gap-5 hover:bg-[#18335A] transition-colors duration-300 border-b border-[#C0C5CE]/10"
                style={{ outline: "none" }}
              >
                <span
                  className="w-10 h-10 border flex items-center justify-center shrink-0"
                  style={{ borderColor: tone.color }}
                  aria-hidden
                >
                  <ToneIcon
                    strokeWidth={1}
                    className="w-5 h-5"
                    style={{ color: tone.color }}
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#9AA4B2]">
                      {s.code}
                    </span>
                    <span
                      className="text-[10px] tracking-[0.18em] uppercase"
                      style={{ color: tone.color }}
                    >
                      {s.riskLabel}
                    </span>
                  </div>
                  <div
                    className="text-xl text-white truncate"
                    style={{
                      fontFamily: "Fraunces, serif",
                      fontWeight: 400,
                    }}
                  >
                    {s.name}
                  </div>
                  <div className="text-[11px] tracking-[0.14em] uppercase text-[#9AA4B2] truncate mt-0.5">
                    {s.tagline} · {s.lifecycleStage}
                  </div>
                </div>
                <ChevronRight
                  strokeWidth={1.25}
                  className="w-5 h-5 text-[#9AA4B2] shrink-0"
                />
              </button>
            </li>
          );
        })}
      </ul>

      <div
        className="px-8 py-5 flex items-center justify-between"
        style={{ borderTop: "1px solid rgba(192,197,206,0.15)" }}
      >
        <span className="text-[11px] tracking-[0.18em] uppercase text-[#9AA4B2]">
          {systems.length} systemen in register
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] tracking-[0.2em] uppercase text-[#D4D8DF] hover:text-white"
          data-testid="system-picker-cancel"
        >
          Annuleren
        </button>
      </div>
    </div>
  </div>
);

export default AssessmentChoice;
